import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { supabase } from '@/lib/supabase';
import { goldTypes } from '@/mocks/goldPrices';

interface Wallet {
  balance: number;
  balance_locked: number;
  total_profit_loss: number;
}

interface LedgerEntry {
  id: string;
  type: string;
  amount: number;
  description: string | null;
  created_at: string;
}

export default function Dashboard() {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user.id;
        if (!userId) {
          setLoading(false);
          return;
        }

        // Lấy ví + lịch sử giao dịch gần đây
        const [{ data: walletRaw }, { data: ledgerRaw }] = await Promise.all([
          supabase.rpc('get_user_wallet_json', { p_user_id: userId }),
          supabase.rpc('get_user_ledger_json', { p_user_id: userId }),
        ]);

        const wallets = Array.isArray(walletRaw) ? walletRaw : JSON.parse((walletRaw as string) || '[]');
        const ledgers = Array.isArray(ledgerRaw) ? ledgerRaw : JSON.parse((ledgerRaw as string) || '[]');

        setWallet(wallets[0] || { balance: 0, balance_locked: 0, total_profit_loss: 0 });
        setLedger(ledgers.slice(0, 5));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Không thể tải dữ liệu.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const balance = wallet?.balance ?? 0;
  const profitLoss = wallet?.total_profit_loss ?? 0;
  const totalAssets = balance + (wallet?.balance_locked ?? 0);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'deposit': return 'Nạp tiền';
      case 'withdraw': return 'Rút tiền';
      case 'buy': return 'Mua vàng';
      case 'sell': return 'Bán vàng';
      case 'fee': return 'Phí giao dịch';
      case 'profit': return 'Lợi nhuận';
      case 'loss': return 'Lỗ';
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit': return 'ri-arrow-down-line';
      case 'withdraw': return 'ri-arrow-up-line';
      case 'buy': return 'ri-shopping-cart-line';
      case 'sell': return 'ri-store-line';
      default: return 'ri-exchange-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'deposit': return { bg: 'bg-emerald-500/15', icon: 'text-emerald-400' };
      case 'withdraw': return { bg: 'bg-rose-500/15', icon: 'text-rose-400' };
      case 'buy': return { bg: 'bg-primary-500/15', icon: 'text-primary-400' };
      case 'sell': return { bg: 'bg-accent-500/15', icon: 'text-accent-400' };
      default: return { bg: 'bg-secondary-500/15', icon: 'text-secondary-400' };
    }
  };

  const getAmountColor = (type: string, amount: number) => {
    if (['deposit', 'sell', 'profit'].includes(type)) return 'text-emerald-500';
    if (['withdraw', 'buy', 'fee', 'loss'].includes(type)) return 'text-rose-500';
    return amount >= 0 ? 'text-emerald-500' : 'text-rose-500';
  };

  const getAmountPrefix = (type: string, amount: number) => {
    if (['deposit', 'sell', 'profit'].includes(type)) return '+';
    if (['withdraw', 'buy', 'fee', 'loss'].includes(type)) return '-';
    return amount >= 0 ? '+' : '';
  };

  return (
    <AccountLayout>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-6">Bảng điều khiển</h1>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
            <div className="flex items-center gap-2 text-foreground-600 text-xs mb-1">
              <i className="ri-copper-coin-line text-base"></i> Tổng tài sản
            </div>
            <p className="text-xl md:text-2xl font-bold text-gold-gradient whitespace-nowrap">
              {loading ? '...' : `${totalAssets.toLocaleString('vi-VN')}đ`}
            </p>
          </div>
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
            <div className="flex items-center gap-2 text-foreground-600 text-xs mb-1">
              <i className="ri-line-chart-line text-base"></i> Lợi nhuận
            </div>
            <p className={`text-xl md:text-2xl font-bold whitespace-nowrap ${profitLoss >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
              {loading ? '...' : `${profitLoss >= 0 ? '+' : ''}${profitLoss.toLocaleString('vi-VN')}đ`}
            </p>
          </div>
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
            <div className="flex items-center gap-2 text-foreground-600 text-xs mb-1">
              <i className="ri-wallet-3-line text-base"></i> Số dư khả dụng
            </div>
            <p className="text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap">
              {loading ? '...' : `${balance.toLocaleString('vi-VN')}đ`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground-950">Giao dịch gần đây</h2>
                <Link to="/tai-san" className="text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap">Xem tất cả</Link>
              </div>
              {loading ? (
                <div className="py-8 text-center text-sm text-foreground-600">Đang tải...</div>
              ) : ledger.length === 0 ? (
                <div className="py-8 text-center text-sm text-foreground-600">
                  <i className="ri-inbox-line text-2xl mb-2 block text-foreground-500"></i>
                  Chưa có giao dịch nào
                </div>
              ) : (
                <div className="space-y-3">
                  {ledger.map((t) => {
                    const color = getTypeColor(t.type);
                    const amountColor = getAmountColor(t.type, t.amount);
                    const prefix = getAmountPrefix(t.type, t.amount);
                    return (
                      <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-background-200/40 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${color.bg}`}>
                            <i className={`text-base ${getTypeIcon(t.type)} ${color.icon}`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground-950">{getTypeLabel(t.type)}</p>
                            <p className="text-xs text-foreground-600">{formatDate(t.created_at)}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-semibold whitespace-nowrap ${amountColor}`}>
                          {prefix}{Math.abs(t.amount).toLocaleString('vi-VN')}đ
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <h2 className="text-sm font-semibold text-foreground-950 mb-4">Giá vàng nổi bật</h2>
              <div className="space-y-3">
                {goldTypes.slice(0, 4).map((g) => (
                  <div key={g.id} className="flex items-center justify-between text-sm">
                    <span className="text-foreground-700 whitespace-nowrap">{g.code}</span>
                    <span className={`font-semibold whitespace-nowrap ${g.change >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {g.sell.toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                ))}
              </div>
              <Link to="/thi-truong" className="mt-4 block text-center text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer">Xem thị trường →</Link>
            </div>

            <div className="rounded-xl border border-primary-500/30 bg-gradient-to-br from-background-100 to-primary-500/10 p-5">
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">Bắt đầu giao dịch</h3>
              <p className="text-xs text-foreground-600 mb-4">Mua bán vàng với giá thời gian thực.</p>
              <Link to="/giao-dich" className="block text-center px-4 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors">
                Giao dịch ngay
              </Link>
            </div>

            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">Nạp / Rút tiền</h3>
              <div className="grid grid-cols-2 gap-2">
                <Link to="/nap-tien" className="block text-center px-4 py-2.5 rounded-md bg-emerald-500 text-background-950 text-sm font-semibold hover:bg-emerald-600 whitespace-nowrap cursor-pointer transition-colors">
                  Nạp tiền
                </Link>
                <Link to="/rut-tien" className="block text-center px-4 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-foreground-700 text-sm font-semibold hover:bg-background-200/60 whitespace-nowrap cursor-pointer transition-colors">
                  Rút tiền
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}